import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { shareTender, unshareTender } from '../../services/api';
import { PERMISSIONS } from '../../shared/constants/permissions';
import type { User } from '../../types/auth';
import type { SectionUserRef, Tender } from '../../types/tender';
import { getUserAvatarColor } from '../../utils/avatarUtils';
import { ManagePermissionsDialog } from '../Dialogs/ManagePermissionsDialog';
import { TenderPermissionsForm } from './TenderPermissionsForm';

const EMPTY_OBJECT: Record<string, never> = {};

interface TenderTeamCardProps {
  tender: Tender;
  contributors: SectionUserRef[];
  users: User[];
  currentUser?: User | null;
  onUpdate?: (field: keyof Tender, value: Tender[keyof Tender]) => Promise<void>;
}

const EMPTY_ARRAY: never[] = [];

const TenderTeamCard: React.FC<TenderTeamCardProps> = ({
  tender,
  contributors,
  users,
  currentUser,
  onUpdate,
}) => {
  const theme = useTheme();
  const { hasPermission } = useAccount();
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState<string>('');

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedShareUser, setSelectedShareUser] = useState<string>('');
  const [isSharing, setIsSharing] = useState(false);
  const [sharePermissions, setSharePermissions] = useState<string[]>([]);

  const [permissionDialogUser, setPermissionDialogUser] = useState<{
    uid: string;
    name: string;
  } | null>(null);

  const handleTransferClick = () => {
    setSelectedNewOwner('');
    setIsTransferDialogOpen(true);
  };

  const handleShareClick = () => {
    setSelectedShareUser('');
    setSharePermissions([PERMISSIONS.TENDER_EDIT]);
    setIsShareDialogOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (onUpdate && selectedNewOwner) {
      await onUpdate('ownerId', selectedNewOwner);
    }
    setIsTransferDialogOpen(false);
  };

  const handleConfirmShare = async () => {
    if (!selectedShareUser) {
      return;
    }
    const targetUser = users.find((u) => u.uid === selectedShareUser);
    if (!targetUser?.publicKey) {
      return;
    }

    setIsSharing(true);
    try {
      await shareTender(tender.id || '', targetUser.uid, targetUser.publicKey);
      if (sharePermissions.length > 0 && onUpdate) {
        const currentMap = tender.memberPermissions || EMPTY_OBJECT;
        const updatedMap = { ...currentMap, [targetUser.uid]: sharePermissions };
        await onUpdate('memberPermissions', updatedMap);
      }
    } catch (_e) {
      // Failed to share tender
    } finally {
      setIsSharing(false);
      setIsShareDialogOpen(false);
    }
  };

  const handleUnshare = async (userId: string) => {
    if (!tender.id) {
      return;
    }
    try {
      await unshareTender(tender.id, userId);
    } catch (_e) {
      // Failed to unshare
    }
  };

  const handleOpenPermissions = (uid: string, name: string) => {
    setPermissionDialogUser({ uid, name });
  };

  const handleSavePermissions = async (userId: string, permissions: string[]) => {
    if (onUpdate) {
      const currentMap = tender.memberPermissions || EMPTY_OBJECT;
      const updatedMap = { ...currentMap, [userId]: permissions };
      await onUpdate('memberPermissions', updatedMap);
    }
  };

  const owner = users.find((u) => u.uid === tender.ownerId);
  const ownerName = owner?.displayName || 'Unknown';
  const ownerEmail = owner?.email || '';

  const linkedPartners =
    tender.alignmentAnalysis?.recommendedPartners?.filter(
      (p) => p.userId && tender.sharedWith?.includes(p.userId)
    ) || EMPTY_ARRAY;

  const linkedPartnerUserIds = linkedPartners.map((p) => p.userId).filter(Boolean) as string[];

  const sharedUsers = users.filter(
    (u) => tender.sharedWith?.includes(u.uid) && !linkedPartnerUserIds.includes(u.uid)
  );

  const canViewAll = hasPermission(PERMISSIONS.TENDER_VIEW_CLIENT);
  const canManageTeam = hasPermission(PERMISSIONS.TENDER_MANAGE_TEAM);

  const canTransfer =
    currentUser &&
    (currentUser.uid === tender.ownerId ||
      (canManageTeam && (canViewAll || currentUser.teamId === tender.teamId)));

  const canShare = canTransfer; // Same permission logic for sharing

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          Team & Ownership
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canShare && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={handleShareClick}
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Share
            </Button>
          )}
          {canTransfer && onUpdate && (
            <Button
              variant="contained"
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={handleTransferClick}
              sx={{
                backgroundColor: theme.palette.greyContrast,
                color: theme.palette.getContrastText(theme.palette.greyContrast),
                '&:hover': {
                  backgroundColor: theme.palette.greyLight,
                },
              }}
            >
              Transfer
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Owner Section */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Owner
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                ...getUserAvatarColor(ownerName),
              }}
            >
              {ownerName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {ownerName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {ownerEmail}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Contributors Section */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Contributors
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {contributors.length > 0 ? (
              contributors.map((contributor) => {
                const avatarStyle = getUserAvatarColor(contributor.displayName || 'U');
                return (
                  <Tooltip key={contributor.uid} title={contributor.email || ''}>
                    <Chip
                      avatar={
                        <Avatar
                          style={{
                            backgroundColor: avatarStyle.bgcolor,
                            color: avatarStyle.color,
                          }}
                        >
                          {(contributor.displayName || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      label={contributor.displayName}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No active contributors yet.
              </Typography>
            )}
          </Box>
        </Box>

        {/* External Partners Section */}
        {linkedPartners.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              External Partners
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {linkedPartners.map((partner) => {
                const avatarStyle = getUserAvatarColor(partner.companyName || 'P');
                return (
                  <Box
                    key={partner.partnerId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      pr: canShare && partner.userId ? 0.5 : 0,
                      pl: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Tooltip title={partner.contactEmail || ''}>
                      <Chip
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: avatarStyle.bgcolor,
                              color: avatarStyle.color,
                            }}
                          >
                            {(partner.companyName || 'P').charAt(0).toUpperCase()}
                          </Avatar>
                        }
                        label={partner.companyName}
                        size="small"
                        variant="outlined"
                        sx={{ border: 'none' }}
                        {...(canShare && partner.userId
                          ? { onDelete: () => partner.userId && handleUnshare(partner.userId) }
                          : {})}
                      />
                    </Tooltip>
                    {canShare && partner.userId && (
                      <Tooltip title="Manage Permissions">
                        <IconButton
                          size="small"
                          onClick={() =>
                            partner.userId &&
                            handleOpenPermissions(partner.userId, partner.companyName || 'Partner')
                          }
                          sx={{ p: 0.5 }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Shared With Section */}
        {sharedUsers.length > 0 || linkedPartners.length === 0 ? (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Shared With (Internal)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {sharedUsers.length > 0 ? (
                sharedUsers.map((user) => {
                  const avatarStyle = getUserAvatarColor(user.displayName || 'U');
                  return (
                    <Box
                      key={user.uid}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 4,
                        pr: canShare ? 0.5 : 0,
                        pl: 0,
                        overflow: 'hidden',
                      }}
                    >
                      <Tooltip title={user.email || ''}>
                        <Chip
                          avatar={
                            <Avatar
                              style={{
                                backgroundColor: avatarStyle.bgcolor,
                                color: avatarStyle.color,
                              }}
                            >
                              {(user.displayName || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                          }
                          label={user.displayName}
                          size="small"
                          variant="outlined"
                          sx={{ border: 'none' }}
                          {...(canShare ? { onDelete: () => handleUnshare(user.uid) } : {})}
                        />
                      </Tooltip>
                      {canShare && (
                        <Tooltip title="Manage Permissions">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleOpenPermissions(user.uid, user.displayName || 'User')
                            }
                            sx={{ p: 0.5 }}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Not shared with anyone else.
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}
      </Box>

      {/* Transfer Dialog */}
      <Dialog
        open={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Tender Ownership</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a new owner for this tender. You will lose ownership rights but may remain as a
            contributor or viewer depending on your role.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="new-owner-select-label">New Owner</InputLabel>
            <Select
              labelId="new-owner-select-label"
              value={selectedNewOwner}
              label="New Owner"
              onChange={(e) => setSelectedNewOwner(e.target.value)}
            >
              {users
                .filter((u) => u.uid !== tender.ownerId) // Exclude current owner
                .map((user) => (
                  <MenuItem key={user.uid} value={user.uid}>
                    {user.displayName} ({user.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTransferDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmTransfer}
            variant="contained"
            color="primary"
            disabled={!selectedNewOwner}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Tender</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a user to share this tender with. They will be granted view and edit access.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="share-user-select-label">Select User</InputLabel>
            <Select
              labelId="share-user-select-label"
              value={selectedShareUser}
              label="Select User"
              onChange={(e) => setSelectedShareUser(e.target.value)}
            >
              {users
                .filter(
                  (u) =>
                    u.uid !== tender.ownerId && !tender.sharedWith?.includes(u.uid) && u.publicKey
                ) // Exclude owner, already shared, and users without keys
                .map((user) => (
                  <MenuItem key={user.uid} value={user.uid}>
                    {user.displayName} ({user.email})
                  </MenuItem>
                ))}
            </Select>
            {users.some((u) => !u.publicKey) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Note: Some users may not appear if they haven't set up encryption keys yet.
              </Typography>
            )}
          </FormControl>

          {selectedShareUser && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}
              >
                PERMISSIONS FOR THIS TENDER
              </Typography>
              <TenderPermissionsForm
                permissions={sharePermissions}
                onChange={setSharePermissions}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsShareDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmShare}
            variant="contained"
            color="primary"
            disabled={!selectedShareUser || isSharing}
          >
            {isSharing ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      {permissionDialogUser && (
        <ManagePermissionsDialog
          open={!!permissionDialogUser}
          onClose={() => setPermissionDialogUser(null)}
          userId={permissionDialogUser.uid}
          userName={permissionDialogUser.name}
          currentPermissions={tender.memberPermissions?.[permissionDialogUser.uid] || EMPTY_ARRAY}
          onSave={handleSavePermissions}
        />
      )}
    </Paper>
  );
};

export default TenderTeamCard;
